import { fieldConfig } from "@autoform/zod";
import { z } from "zod";

const disabled = fieldConfig({ inputProps: { disabled: true } });

/**
 * One cast entry as `UpdatePerformanceCommand` takes it: the raw `PerformanceCastDbDto`,
 * written straight through to Firestore. `personId`, `name` and `department` are all
 * REQUIRED there, so — unlike a play credit — a name-only entry is not representable.
 * That is the backend gap spec §3.4 records: an unlinked stage actor survives on the play
 * and is dropped from the performance.
 */
export const performanceCastNestedSchema = z.object({
  personId: z.string().min(1),
  name: z.string().min(1),
  role: z.string().nullish(),
  department: z.string().default("Acting"),
  order: z.coerce.number().nullish(),
  photo: z.object({ url: z.string(), blurhash: z.string() }).nullish(),
});

export type PerformanceCastNestedType = z.infer<
  typeof performanceCastNestedSchema
>;

/**
 * Edit schema for a Performance, bound to `PerformanceDbDto` but modelling ONLY what
 * `UpdatePerformanceCommand` accepts today: startDate, cast, status, tags, hide. Play and
 * venue are denormalized copies the backend re-derives — editing them here would be a lie.
 *
 * `startDate` is restamped through the venue timezone by `performance-start-date.ts` on the
 * way in and out; the schema itself holds the picker's browser-local ISO string.
 */
export const basePerformanceEditSchema = z.object({
  id: z.string().nullish().superRefine(disabled),
  startDate: z
    .string()
    .datetime({ offset: true })
    .superRefine(fieldConfig({ fieldType: "datetime", label: "Start date" })),
  cast: z
    .array(performanceCastNestedSchema)
    .default([])
    .superRefine(fieldConfig({ fieldType: "performanceCast" })),
  status: z.enum(["Draft", "Complete", "Cancelled"]),
  tags: z
    .array(z.string())
    .nullish()
    .default([])
    .superRefine(fieldConfig({ fieldType: "tagList" })),
  hide: z.boolean().default(false),
});

/**
 * `ticketUrl`, `pricing` and `screen` ship on `PerformanceDbDto` as of admin-api 4.12.0,
 * which serializes all three as `null` rather than omitting the key — so `Object.hasOwn`
 * already sees them on a current performance, and Tina writes `ticketUrl` and a one-entry
 * `pricing` on every performance it creates. No feature flag: presence detection now exists
 * as a tolerance for a document written by an older admin-api, not a wait for the fields to
 * ship.
 */
export interface PerformanceOptionalFieldPresence {
  ticketUrl: boolean;
  pricing: boolean;
  screen: boolean;
}

export function performanceFieldPresence(
  raw: unknown,
): PerformanceOptionalFieldPresence {
  const record = typeof raw === "object" && raw !== null ? raw : {};
  return {
    ticketUrl: Object.hasOwn(record, "ticketUrl"),
    pricing: Object.hasOwn(record, "pricing"),
    screen: Object.hasOwn(record, "screen"),
  };
}

export function buildPerformanceEditSchema(
  present: PerformanceOptionalFieldPresence,
) {
  let schema = basePerformanceEditSchema;
  if (present.ticketUrl) {
    schema = schema.extend({
      ticketUrl: z
        .string()
        .url("Must be an http(s) link")
        .nullish()
        .superRefine(fieldConfig({ label: "Ticket URL" })),
    });
  }
  if (present.pricing) {
    schema = schema.extend({
      // `UpdatePerformanceCommandValidator`: `RuleForEach(Pricing)` requires `Label`
      // `NotEmpty`, `Amount` `NotNull().GreaterThanOrEqualTo(0)`, `Currency` matching
      // `^[A-Z]{3}$` — every member is required once a pricing entry is sent at all.
      pricing: z
        .array(
          z.object({
            label: z.string().min(1, "Required"),
            amount: z.coerce.number().min(0),
            currency: z
              .string()
              .regex(/^[A-Z]{3}$/, "Must be a 3-letter ISO 4217 code")
              .default("EUR"),
          }),
        )
        .nullish()
        .default([]),
    });
  }
  if (present.screen) {
    schema = schema.extend({
      // Same validator, `When(Screen is not null)`: `Screen.Name` `NotEmpty`,
      // `Screen.Type` `NotNull().IsInEnum()` against `ScreenTypeDb` (`Indoor` | `OpenAir`).
      screen: z
        .object({
          name: z.string().min(1, "Required"),
          type: z.enum(["Indoor", "OpenAir"]),
        })
        .nullish(),
    });
  }
  return schema;
}

export type PerformanceEditFormType = z.infer<
  typeof basePerformanceEditSchema
> &
  Partial<{
    ticketUrl: string | null;
    pricing: Array<{
      label: string;
      amount: number;
      currency: string;
    }> | null;
    screen: { name: string; type: "Indoor" | "OpenAir" } | null;
  }>;
